import { ClickAwayListener } from "@mui/material";
import { useMemo, useState } from "react";
import { PopperArrow, PopperWithArrow } from "../PopperWithArrow";

type CalendarPopupProps = {
  children: any;
  isOpen: boolean;
  visitAnchor: any;
  onClose: () => void;
};

type AnchorNode = {
  isConnected?: boolean;
  getBoundingClientRect?: () => DOMRect;
};

const isUsableRect = (rect?: DOMRect) => !!rect && (rect.width > 0 || rect.height > 0);

/**
 * Calendar events re-render while a popup is open, because saving a change refetches the
 * timeline data, and that can detach the event element the popup is anchored to. A detached
 * node measures as 0x0, which Popper reads as the top left corner of the viewport, so fall
 * back to the last real measurement and leave the popup where the user opened it.
 */
export const createResilientAnchor = (node: AnchorNode) => {
  let lastRect: DOMRect | undefined;

  return {
    getBoundingClientRect: () => {
      const rect = node.getBoundingClientRect?.();

      if (node.isConnected !== false && isUsableRect(rect)) {
        lastRect = rect;
      }

      return (lastRect ?? rect) as DOMRect;
    },
  };
};

export const CalendarPopup = ({
  isOpen,
  visitAnchor,
  children,
  onClose,
}: CalendarPopupProps) => {
  const [arrowRef, setArrowRef] = useState(null);
  const anchor = useMemo(
    () => (visitAnchor ? createResilientAnchor(visitAnchor) : null),
    [visitAnchor],
  );

  if (!anchor) {
    return null;
  }

  return (
    <>
      <PopperWithArrow
        open={isOpen}
        anchorEl={anchor}
        placement="auto"
        modifiers={[
          {
            name: "arrow",
            enabled: true,
            options: {
              element: arrowRef,
            },
          },
          // Keep the popup within the viewport on both axes. Without altAxis,
          // a tall popup anchored to a row low on the screen (e.g. the 4th+
          // visit worklist item) flips to a top/left/right placement and its
          // top edge pops off the top of the screen. altAxis + untethered
          // shifts it back into view.
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              tether: false,
              padding: 8,
            },
          },
          {
            name: "flip",
            enabled: true,
            options: {
              padding: 8,
            },
          },
        ]}
        style={{
          zIndex: 1205,
        }}
      >
        <PopperArrow ref={setArrowRef} className="MuiPopper-arrow" />
        <ClickAwayListener onClickAway={onClose}>
          <>{children}</>
        </ClickAwayListener>
      </PopperWithArrow>
    </>
  );
};
